import {
  countWords,
  splitParagraphs,
  splitSentences,
} from "@/lib/tool-kit/admissions/counts";
import { allocateWords } from "@/lib/tool-kit/essay-templates/research-proposal";

/**
 * The Common App personal essay for the 2026–2027 application cycle.
 *
 * Source: Common App, "Announcing the 2026–2027 Common App essay prompts" (27 February
 * 2026: the seven prompts carry over unchanged) and its essay-prompts page. The essay is
 * between 250 and 650 words; the application will not accept fewer than 250, and 650 is
 * a limit rather than a target. Common App does not publish how its counter treats a
 * dash or a hyphenated word, so the count here is the usual one (see `countWords`) and
 * the page advises a margin.
 */

export const COMMON_APP_MIN_WORDS = 250;
export const COMMON_APP_MAX_WORDS = 650;
export const COMMON_APP_DEFAULT_TARGET = 600;
export const COMMON_APP_CYCLE = "2026–2027";

export const COMMON_APP_PROMPTS = [
  "p1",
  "p2",
  "p3",
  "p4",
  "p5",
  "p6",
  "p7",
] as const;
export type CommonAppPrompt = (typeof COMMON_APP_PROMPTS)[number];

export const OUTLINE_STEPS = [
  "scene",
  "context",
  "turn",
  "reflection",
  "close",
] as const;
export type OutlineStep = (typeof OUTLINE_STEPS)[number];

/** A planning split, in percent: most of the weight on what happened and what it meant. */
export const OUTLINE_SHARES: Record<OutlineStep, number> = {
  scene: 15,
  context: 20,
  turn: 25,
  reflection: 30,
  close: 10,
};

/** The target a writer asked for, kept inside what the application accepts. */
export function clampTarget(target: number): number {
  return Math.min(
    COMMON_APP_MAX_WORDS,
    Math.max(COMMON_APP_MIN_WORDS, Math.round(target))
  );
}

/** Word targets for the five outline steps; they add up to the target, in fives. */
export function outlineTargets(target: number): Record<OutlineStep, number> {
  const words = allocateWords(
    clampTarget(target),
    OUTLINE_STEPS.map((step) => OUTLINE_SHARES[step]),
    5
  );
  const targets = {} as Record<OutlineStep, number>;
  OUTLINE_STEPS.forEach((step, index) => {
    targets[step] = words[index];
  });
  return targets;
}

export type EssayStatus = "empty" | "under" | "in" | "over";

export interface EssayAnalysis {
  words: number;
  status: EssayStatus;
  /** Words still needed to reach 250; 0 once reached. */
  toMinimum: number;
  /** Words left before 650; negative when over. */
  toMaximum: number;
  paragraphs: number;
  sentences: number;
  averageSentenceWords: number;
  longestSentenceWords: number;
  /** Sentences that open with the word "I", as a whole-number percentage. */
  startsWithIPercent: number;
}

const OPENS_WITH_I = /^[("“'‘]*I(?![\p{L}])/u;

function statusOf(words: number): EssayStatus {
  if (words === 0) {
    return "empty";
  }
  if (words < COMMON_APP_MIN_WORDS) {
    return "under";
  }
  return words > COMMON_APP_MAX_WORDS ? "over" : "in";
}

/** Count a draft against the 250–650 range and describe its sentences. */
export function analyseEssay(text: string): EssayAnalysis {
  const words = countWords(text);
  const sentences = splitSentences(text);
  const lengths = sentences.map((sentence) => countWords(sentence));
  const totalWords = lengths.reduce((sum, length) => sum + length, 0);
  const openers = sentences.filter((sentence) => OPENS_WITH_I.test(sentence));
  return {
    words,
    status: statusOf(words),
    toMinimum: Math.max(0, COMMON_APP_MIN_WORDS - words),
    toMaximum: COMMON_APP_MAX_WORDS - words,
    paragraphs: splitParagraphs(text).length,
    sentences: sentences.length,
    averageSentenceWords:
      sentences.length === 0
        ? 0
        : Math.round((totalWords / sentences.length) * 10) / 10,
    longestSentenceWords: Math.max(0, ...lengths),
    startsWithIPercent:
      sentences.length === 0
        ? 0
        : Math.round((openers.length / sentences.length) * 100),
  };
}
